import { isValidElement, useCallback, useRef } from 'react';

import useModalEnterKey from './useModalEnterKey';
import useRevealFirstError from './useRevealFirstError';

// Destructive or cautionary primaries (delete, void, refund…) still need a deliberate click.
const CLICK_ONLY_COLORS = ['danger', 'warning'];

/**
 * Form ergonomics for every Modal:
 * - Enter (including from a single-line input) runs `onEnter`, else `footerActions.primary` —
 *   unless that primary is `danger`/`warning` colored or the modal sets `submitOnEnter={false}`.
 * - Enter or a `footerActions.primary` click scrolls to the first field showing a validation
 *   error (with a custom `footer`, any of its buttons; a JSX-element primary is left alone).
 */
const useModalSubmit = ({
  isOpen,
  isFooter,
  footerActions,
  onEnter,
  submitOnEnter,
  isSubModalOpen
}) => {
  const contentRef = useRef(null);
  const bodyRef = useRef(null);
  const watchForErrors = useRevealFirstError(bodyRef);

  const setContentRef = useCallback((node) => {
    contentRef.current = node;
  }, []);

  const primary = onEnter ? { onClick: onEnter } : footerActions?.primary;
  const canSubmitOnEnter =
    submitOnEnter &&
    !isSubModalOpen &&
    !isValidElement(primary) &&
    !CLICK_ONLY_COLORS.includes(primary?.color) &&
    typeof primary?.onClick === 'function';

  useModalEnterKey({
    isOpen,
    isFooter: isFooter || !!onEnter,
    containerRef: contentRef,
    allowTextInputs: true,
    button: canSubmitOnEnter
      ? {
          ...primary,
          onClick: () => {
            watchForErrors();
            primary.onClick();
          }
        }
      : null
  });

  const footerPrimary = footerActions?.primary;
  const submitFooterActions =
    typeof footerPrimary?.onClick === 'function'
      ? {
          ...footerActions,
          primary: {
            ...footerPrimary,
            onClick: (...args) => {
              watchForErrors();
              return footerPrimary.onClick(...args);
            }
          }
        }
      : footerActions;

  // A custom footer's primary can't be told apart from its other buttons, so any click counts.
  const onSubmitClickCapture = (event) => {
    if (event.target.closest('button')) watchForErrors();
  };

  return { setContentRef, bodyRef, submitFooterActions, onSubmitClickCapture };
};

export default useModalSubmit;
